import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Settings as SettingsIcon,
  Building2,
  Users,
  MapPin,
  Shield,
  FileText,
  Package,
  Car,
  Wrench,
  UserCog,
  Calendar,
  Database,
  Download,
  AlertTriangle,
} from "lucide-react";

const SettingsOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [bootstrapVarsPresent, setBootstrapVarsPresent] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/bootstrap-warning`, { withCredentials: true })
      .then((res) => setBootstrapVarsPresent(res.data?.bootstrap_vars_present === true))
      .catch(() => {});
  }, [isAdmin]);

  const handleExport = () => {
    window.location.href = `${API}/backup/export?include_attachments=${includeAttachments}`;
  };

  const handleItemClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const SettingCard = ({ title, items }) => (
    <Card className="border border-gray-200 rounded-sm shadow-none">
      <CardContent className="p-4">
        <h3 className="font-heading font-bold text-base tracking-tight mb-3">
          {title}
        </h3>
        <div className="space-y-2">
          {items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <button
                key={idx}
                onClick={() => handleItemClick(item.path)}
                disabled={!item.path}
                className={`w-full text-left py-2 px-3 rounded-sm text-sm flex items-center gap-3 transition-colors ${
                  item.path
                    ? "hover:bg-gray-100 cursor-pointer"
                    : "cursor-not-allowed hover:text-red-600"
                }`}
              >
                <Icon className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-black text-white rounded-sm flex items-center justify-center">
          <SettingsIcon className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter uppercase">
            Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure your system preferences</p>
        </div>
      </div>

      {/* Bootstrap credentials still in the environment — Admin only */}
      {isAdmin && bootstrapVarsPresent && (
        <Card
          className="border border-amber-400 bg-amber-50 rounded-sm shadow-none"
          data-testid="bootstrap-warning-card"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <h3 className="font-heading font-bold text-base tracking-tight">
                Bootstrap credentials still set
              </h3>
              <p className="text-sm text-gray-700 mt-1">
                BOOTSTRAP_ADMIN_USERNAME and BOOTSTRAP_ADMIN_PASSWORD are still present in the
                server environment. Remove them: if the users collection is ever emptied, an
                Admin with that known password will be recreated on the next restart.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Settings */}
      <div className="space-y-4">
        <h2 className="text-sm uppercase tracking-widest text-gray-500">
          Organization Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SettingCard
            title="Organization"
            items={[
              { label: "Company Details", path: null, icon: Building2 },
              { label: "Branches", path: "/branches", icon: MapPin },
              { label: "Fiscal Year", path: null, icon: Calendar },
            ]}
          />
          <SettingCard
            title="Users & Roles"
            items={[
              { label: "Users", path: "/users", icon: Users },
              { label: "Roles", path: "/roles", icon: Shield },
            ]}
          />
          <SettingCard
            title="General"
            items={[
              { label: "Templates", path: null, icon: FileText },
              { label: "Other Settings", path: "/other-settings", icon: SettingsIcon },
            ]}
          />
          {isAdmin && (
            <Card
              className="border border-gray-200 rounded-sm shadow-none"
              data-testid="data-backup-card"
            >
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-base tracking-tight mb-3">
                  Data &amp; Backup
                </h3>
                <div className="flex items-start gap-3 py-2 px-3 text-sm">
                  <Database className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="space-y-3">
                    <p className="text-gray-500">
                      Downloads every collection as a restorable ZIP.
                    </p>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="include-attachments"
                        checked={includeAttachments}
                        onCheckedChange={(v) => setIncludeAttachments(v === true)}
                        data-testid="include-attachments-checkbox"
                      />
                      <Label
                        htmlFor="include-attachments"
                        className="text-sm font-normal leading-snug cursor-pointer"
                      >
                        Include file attachments (policy PDFs, vehicle documents)
                      </Label>
                    </div>
                    <Button
                      onClick={handleExport}
                      className="rounded-sm h-9"
                      data-testid="export-all-data-btn"
                    >
                      <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Export All Data
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Module Settings */}
      <div className="space-y-4 pt-4">
        <h2 className="text-sm uppercase tracking-widest text-gray-500">
          Module Settings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SettingCard
            title="Customer Relations"
            items={[
              { label: "Appointment Settings", path: null, icon: Calendar },
              { label: "Workflow Rules", path: null, icon: FileText },
            ]}
          />
          <SettingCard
            title="Parts"
            items={[
              { label: "Part Categories", path: null, icon: Package },
              { label: "Inventory Settings", path: null, icon: Package },
            ]}
          />
          <SettingCard
            title="Bodyshop"
            items={[
              { label: "Job Types", path: null, icon: Car },
              { label: "Estimation Templates", path: null, icon: FileText },
            ]}
          />
          <SettingCard
            title="Mechanical"
            items={[
              { label: "Service Types", path: null, icon: Wrench },
              { label: "Maintenance Schedules", path: null, icon: Calendar },
            ]}
          />
          <SettingCard
            title="HR"
            items={[
              { label: "Employee Categories", path: null, icon: UserCog },
              { label: "Leave Types", path: null, icon: Calendar },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default SettingsOverview;
